// Create → preview → post flow for a landed cost doc (BUILD_SPEC.md §5.4,
// §9.3, §8.1 step 5; services/api/src/modules/intake/landed-cost.ts). One
// modal handles "New allocation" (docId=null), "Continue" on an existing DRAFT
// (docId set, editable), and a read-only view of a POSTED/REVERSED doc —
// mirrors features/purchasing-intake/GoodsReceiptDraftModal.tsx's load-if-
// editing idiom and features/purchasing-intake/PurchaseOrderRegister.tsx's
// read-only-once-non-DRAFT branch.
//
// The per-line split is previewed with the EXACT pure function the server
// calls at POST (`allocateLandedCost`, @zebra/core) — same "client preview
// calls the real core function, never a hand-rolled reimplementation" idiom
// features/purchasing-intake/ledgerPreview.ts established for goods-receipt
// payable math — so the number reviewed here can never silently drift from
// what posting actually produces.
//
// Target-line picker is scoped to POSTED goods receipts only: a line's
// `payableCents` (the §9.3 allocation weight) only exists once its receipt has
// posted, so restricting the picker to that pool keeps the preview always
// fully computable. The API itself still allows targeting a still-DRAFT
// receipt's line at create/edit time (POSTED-ness is only enforced at POST —
// see landed-cost.ts's file header, "the landed cost invoice can arrive before
// the receipt is posted") — that case is a deliberate scope simplification
// here and falls back to the raw API, same as before this screen existed.
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Group,
  Modal,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { allocateLandedCost, CoreError, isMassLine } from '@zebra/core';
import { ApiRequestError } from '../../api/client';
import { useCanOperate } from '../../auth/permissions';
import { DocStatusBadge } from '../../components/DocStatusBadge';
import { centsToRand, formatMoneyCents, randToCents } from './format';
import {
  useCreateAndPostLandedCostDoc,
  useCreateLandedCostDoc,
  useLandedCostDoc,
  usePostedGoodsReceipts,
  usePostLandedCostDoc,
  useProducts,
  useSuppliers,
  useUpdateLandedCostDoc,
  type LandedCostDocWriteInput,
} from './api';

interface CandidateLine {
  grId: string;
  grNumber: string;
  lineId: string;
  productId: string;
  /** Mass receipt line's kg — null on an EACH (count) line, which carries `qty`
   * instead (exactly one populated; @zebra/core goodsReceiptLineResponseSchema). */
  qtyKg: string | null;
  qty: string | null;
  payableCents: string;
}

/** A candidate line's quantity with its native unit — EACH lines allocate by
 * payable value exactly like mass lines (§9.3 weight is payableCents), they
 * just display in units rather than kg. */
function formatCandidateQty(line: CandidateLine): string {
  return isMassLine(line) ? `${line.qtyKg} kg` : `${line.qty} units`;
}

export function LandedCostFormModal({ docId, onClose }: { docId: string | null; onClose: () => void }) {
  const canOperate = useCanOperate();
  const { data: doc } = useLandedCostDoc(docId ?? undefined);
  const { data: suppliers } = useSuppliers({ isActive: true });
  const { data: products } = useProducts();
  const { data: postedGrs } = usePostedGoodsReceipts();

  const isEditing = Boolean(docId);
  const create = useCreateLandedCostDoc();
  const createAndPost = useCreateAndPostLandedCostDoc();
  const update = useUpdateLandedCostDoc(docId ?? '');
  const post = usePostLandedCostDoc(docId ?? '');

  const [supplierId, setSupplierId] = useState('');
  const [description, setDescription] = useState('');
  const [amountRand, setAmountRand] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedLineIds, setSelectedLineIds] = useState<Set<string>>(new Set());

  // Populate from an existing doc once it loads. The parent (LandedCostRegister)
  // remounts this modal per-open via `key={docId ?? 'new'}`, so this effect runs
  // exactly once per editing session — mirrors PurchaseOrderRegister's
  // populate-on-load effect.
  useEffect(() => {
    if (!doc) return;
    setSupplierId(doc.supplierId);
    setDescription(doc.description);
    setAmountRand(centsToRand(doc.amountCents));
    setNotes(doc.notes ?? '');
    setSelectedLineIds(new Set(doc.allocations.map((a) => a.grLineId)));
  }, [doc]);

  const readOnly = Boolean(doc && doc.status !== 'DRAFT');

  const productName = useMemo(() => new Map((products?.items ?? []).map((p) => [p.id, `${p.sku} — ${p.name}`])), [products]);

  const candidateLines = useMemo<CandidateLine[]>(() => {
    const rows: CandidateLine[] = [];
    for (const gr of postedGrs?.items ?? []) {
      for (const line of gr.lines) {
        if (line.payableCents == null) continue;
        rows.push({
          grId: gr.id,
          grNumber: gr.number,
          lineId: line.id,
          productId: line.productId,
          qtyKg: line.qtyKg,
          qty: line.qty,
          payableCents: line.payableCents,
        });
      }
    }
    return rows;
  }, [postedGrs]);

  // A doc created/edited outside this screen (raw API) could target a line
  // this pool doesn't contain (not currently POSTED, or beyond the 200-row
  // lookup window) — surface it rather than silently dropping it from the
  // selection when the form re-saves (this form always replaces the full
  // target-line set on submit).
  const knownLineIds = useMemo(() => new Set(candidateLines.map((l) => l.lineId)), [candidateLines]);
  const unknownSelectedIds = useMemo(() => [...selectedLineIds].filter((id) => !knownLineIds.has(id)), [selectedLineIds, knownLineIds]);

  function toggleLine(lineId: string) {
    setSelectedLineIds((prev) => {
      const next = new Set(prev);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  }

  const amountCents = randToCents(amountRand || '0');
  let amountCentsBig: bigint | null = null;
  try {
    amountCentsBig = BigInt(amountCents);
  } catch {
    amountCentsBig = null;
  }

  const selectedLines = candidateLines.filter((l) => selectedLineIds.has(l.lineId));

  let previewByLine: Map<string, bigint> | null = null;
  let previewTotal: bigint | null = null;
  let previewError: string | null = null;
  if (amountCentsBig !== null && amountCentsBig > 0n && selectedLines.length > 0) {
    try {
      const allocated = allocateLandedCost(
        amountCentsBig,
        selectedLines.map((l) => ({ id: l.lineId, valueCents: BigInt(l.payableCents) })),
      );
      previewByLine = new Map(allocated.map((a) => [a.id, a.cents]));
      previewTotal = allocated.reduce((sum, a) => sum + a.cents, 0n);
    } catch (err) {
      previewError = err instanceof CoreError ? err.message : 'Unable to compute the allocation preview.';
    }
  }

  function buildBody(): LandedCostDocWriteInput {
    return {
      supplierId,
      description: description.trim(),
      amountCents,
      grLineIds: [...selectedLineIds],
      notes: notes.trim() || null,
    };
  }

  function handleSaveOnly() {
    const body = buildBody();
    if (isEditing) update.mutate(body, { onSuccess: onClose });
    else create.mutate(body, { onSuccess: onClose });
  }

  function handleSaveAndPost() {
    const body = buildBody();
    if (isEditing) update.mutate(body, { onSuccess: () => post.mutate(undefined, { onSuccess: onClose }) });
    else createAndPost.mutate(body, { onSuccess: onClose });
  }

  const saveOnlyMutation = isEditing ? update : create;
  const saveAndPostPending = isEditing ? update.isPending || post.isPending : createAndPost.isPending;
  const anyError = saveOnlyMutation.error ?? post.error ?? createAndPost.error;

  const supplierOptions = (suppliers?.items ?? []).map((s) => ({ value: s.id, label: `${s.code} — ${s.name}` }));
  const canSubmit = Boolean(supplierId && description.trim() && amountCentsBig !== null && amountCentsBig > 0n && selectedLineIds.size > 0);

  if (readOnly && doc) {
    return (
      <Modal opened onClose={onClose} title={`Landed cost ${doc.number}`} centered size="lg">
        <Stack gap="sm">
          <DocStatusBadge status={doc.status} />
          <Text size="sm" c="dimmed">
            Landed cost docs can only be edited while DRAFT — this one has already {doc.status === 'POSTED' ? 'posted' : 'been reversed'}.
            {doc.status === 'POSTED' && ' Use Reverse from the register if this allocation needs to change.'}
          </Text>
          <Text size="sm">
            {doc.description} — <strong>{formatMoneyCents(doc.amountCents)}</strong>
          </Text>
          <Table.ScrollContainer minWidth={640}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Goods receipt</Table.Th>
                  <Table.Th>Product</Table.Th>
                  <Table.Th>Allocated</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {doc.allocations.map((a) => {
                  const candidate = candidateLines.find((l) => l.lineId === a.grLineId);
                  return (
                    <Table.Tr key={a.grLineId}>
                      <Table.Td>{candidate ? candidate.grNumber : a.grLineId.slice(0, 8)}</Table.Td>
                      <Table.Td>{candidate ? productName.get(candidate.productId) ?? candidate.productId : '—'}</Table.Td>
                      <Table.Td>{a.amountCents != null ? formatMoneyCents(a.amountCents) : '—'}</Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          <Group justify="flex-end">
            <Button onClick={onClose}>Close</Button>
          </Group>
        </Stack>
      </Modal>
    );
  }

  return (
    <Modal opened onClose={onClose} title={isEditing ? 'Continue landed cost allocation (DRAFT)' : 'New landed cost allocation'} centered size="xl">
      <Stack gap="md">
        {anyError && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {anyError instanceof ApiRequestError ? anyError.message : 'Failed to save the landed cost allocation'}
          </Alert>
        )}

        <Group grow align="flex-start">
          <Select
            label="Billing supplier"
            description="Who invoiced this cost (e.g. a haulier) — not necessarily the goods supplier"
            data={supplierOptions}
            searchable
            required
            value={supplierId}
            onChange={(v) => setSupplierId(v ?? '')}
          />
          <TextInput label="Amount (R, ex VAT)" required value={amountRand} onChange={(e) => setAmountRand(e.currentTarget.value)} />
        </Group>
        <TextInput
          label="Description"
          required
          placeholder="e.g. Haulage — June deliveries"
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
        />
        <Textarea label="Notes (optional)" autosize minRows={1} value={notes} onChange={(e) => setNotes(e.currentTarget.value)} />

        <Text fw={500} size="sm">
          Target goods receipt lines
        </Text>
        <Text size="xs" c="dimmed">
          Only POSTED goods receipts are listed — a line&apos;s payable value is the allocation weight (§9.3) and only exists once
          its receipt has posted.
        </Text>
        <Paper withBorder radius="md">
          <ScrollArea.Autosize mah={280}>
            <Table.ScrollContainer minWidth={720}>
              <Table stickyHeader>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th />
                    <Table.Th>Goods receipt</Table.Th>
                    <Table.Th>Product</Table.Th>
                    <Table.Th>Qty</Table.Th>
                    <Table.Th>Payable</Table.Th>
                    <Table.Th>Proposed share</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {candidateLines.map((line) => {
                    const share = previewByLine?.get(line.lineId);
                    return (
                      <Table.Tr key={line.lineId}>
                        <Table.Td>
                          <Checkbox checked={selectedLineIds.has(line.lineId)} onChange={() => toggleLine(line.lineId)} />
                        </Table.Td>
                        <Table.Td>{line.grNumber}</Table.Td>
                        <Table.Td>{productName.get(line.productId) ?? line.productId}</Table.Td>
                        <Table.Td>{formatCandidateQty(line)}</Table.Td>
                        <Table.Td>{formatMoneyCents(line.payableCents)}</Table.Td>
                        <Table.Td>{share != null ? formatMoneyCents(share) : '—'}</Table.Td>
                      </Table.Tr>
                    );
                  })}
                  {candidateLines.length === 0 && (
                    <Table.Tr>
                      <Table.Td colSpan={6}>
                        <Text c="dimmed" ta="center" py="sm">
                          No POSTED goods receipts yet — post one from Receipts first.
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </ScrollArea.Autosize>
        </Paper>

        {unknownSelectedIds.length > 0 && (
          <Alert color="yellow" icon={<IconAlertCircle size={16} />}>
            {unknownSelectedIds.length} target line(s) already on this doc aren&apos;t shown above (not currently POSTED, or outside
            the lookup window) — they stay targeted unless you save, since saving always replaces the full target-line set with
            what&apos;s ticked here.
          </Alert>
        )}

        {previewError && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {previewError}
          </Alert>
        )}

        <Paper withBorder p="sm" radius="md" bg="var(--mantine-color-green-light)">
          <Text size="sm">
            Ledger effect: on Post, a RECOST movement is recorded against each selected line for its share above, splitting{' '}
            <strong>{amountCentsBig !== null ? formatMoneyCents(amountCentsBig) : '—'}</strong> proportional to payable value
            (Hamilton/largest-remainder, §9.3).
            {previewTotal != null && (
              <>
                {' '}
                Total previewed: <strong>{formatMoneyCents(previewTotal)}</strong>.
              </>
            )}
            {previewTotal == null && !previewError && ' Enter an amount and select at least one line to preview the split.'}
          </Text>
        </Paper>

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Close
          </Button>
          <Tooltip label="Requires Operations role" disabled={canOperate}>
            <span>
              <Button variant="light" disabled={!canOperate || !canSubmit} loading={saveOnlyMutation.isPending} onClick={handleSaveOnly}>
                {isEditing ? 'Save draft' : 'Create draft'}
              </Button>
            </span>
          </Tooltip>
          <Tooltip label="Requires Operations role" disabled={canOperate}>
            <span>
              <Button disabled={!canOperate || !canSubmit} loading={saveAndPostPending} onClick={handleSaveAndPost}>
                {isEditing ? 'Save & post' : 'Create & post'}
              </Button>
            </span>
          </Tooltip>
        </Group>
      </Stack>
    </Modal>
  );
}
